import { ClinicalLibrariesReadDtoReturn } from "functions/api/library/getLibraries";
import { useEffect } from "react";
import {
  clinicalLibraryLoaderProps,
  LOAD_CLINICAL_LIBRARY,
  SET_CLINICAL_LIBRARY_LOADING,
} from "./interface";

/**
 * Applies the deferred Clinical Library result returned by the hierarchy loader.
 *
 * The loader owns request creation and cancellation. This effect manages the
 * Clinical Library loading state and applies the result while the associated
 * hierarchy visit is active.
 *
 * @param props - Deferred Clinical Library request and hierarchy dispatcher
 * @param props.clinicalLibraryPromise - Promise for the deferred Clinical Library request
 * @param props.dispatch - Dispatcher for hierarchy state updates
 */
export const useClinicalLibraryLoaderEffect = ({
  clinicalLibraryPromise,
  dispatch,
}: clinicalLibraryLoaderProps): void => {
  useEffect(() => {
    let isActive = true;

    if (!clinicalLibraryPromise) {
      dispatch({
        operation: SET_CLINICAL_LIBRARY_LOADING,
        isClinicalLibraryLoading: false,
      });

      return;
    }

    // The loader has started the deferred CL request,
    // so show the loading state until it completes.
    dispatch({
      operation: SET_CLINICAL_LIBRARY_LOADING,
      isClinicalLibraryLoading: true,
    });

    void clinicalLibraryPromise
      .then((response: ClinicalLibrariesReadDtoReturn) => {
        if (isActive) {
          if (response.success) {
            dispatch({
              operation: LOAD_CLINICAL_LIBRARY,
              clinicalLibrary: response.data,
            });
            return;
          }

          dispatch({
            operation: SET_CLINICAL_LIBRARY_LOADING,
            isClinicalLibraryLoading: false,
          });
        }
      })
      .catch((error: unknown) => {
        /* istanbul ignore next */
        if (isActive) {
          console.warn("Clinical Library loading failed:", error);
          dispatch({
            operation: SET_CLINICAL_LIBRARY_LOADING,
            isClinicalLibraryLoading: false,
          });
        }
      });

    return () => {
      isActive = false;
    };
  }, [clinicalLibraryPromise, dispatch]);
};
